import { ChangeEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Alert, Box, Button, Container, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import { validateToken } from '../utils/Cookie';
import { ItemAmazon, MAX_ITENS_AMAZON, lerPlanilhaAmazon } from '../utils/amazonImages';

export default function ImagensAmazon() {
  const navigate = useNavigate();
  const [itens, setItens] = useState<ItemAmazon[]>([]);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState('');
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!validateToken()) navigate('/login?logout=true');
    return () => controllerRef.current?.abort();
  }, [navigate]);

  const importar = async (event: ChangeEvent<HTMLInputElement>) => {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    if (!arquivo) return;
    setErro('');
    setAviso('');
    setMensagem('');
    setItens([]);
    setNomeArquivo('');
    if (arquivo.size > 5 * 1024 * 1024) {
      setErro('O arquivo deve ter no máximo 5 MB.');
      return;
    }
    try {
      const lidos = lerPlanilhaAmazon(await arquivo.arrayBuffer());
      setItens(lidos);
      setNomeArquivo(arquivo.name);
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Não foi possível ler o arquivo.');
    }
  };

  const baixarModelo = () => {
    const csv = '\uFEFFSKU;ASIN\r\n12345;B012345678\r\n';
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'modelo-imagens-amazon.csv');
  };

  const prepararZip = async () => {
    if (!itens.length || controllerRef.current) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setProcessando(true);
    setErro('');
    setAviso('');
    setMensagem('');
    setProgresso('Consultando e preparando imagens no Portal…');
    try {
      const { data, headers } = await axios.post(`${process.env.REACT_APP_BACKEND}/amazon-imagens/download`, { itens }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob', signal: controller.signal,
        onDownloadProgress: evento => {
          if (!controller.signal.aborted) setProgresso(`Recebendo ZIP · ${(evento.loaded / 1024 / 1024).toFixed(1)} MB`);
        },
      });
      if (controller.signal.aborted) return;
      const quantidadeSemFotos = Number(headers['x-amazon-missing-count'] || 0);
      if (quantidadeSemFotos > 0) {
        let skusSemFotos: string[] = [];
        try {
          const lista = JSON.parse(decodeURIComponent(headers['x-amazon-missing-skus'] || '[]'));
          if (Array.isArray(lista)) skusSemFotos = lista.filter((sku): sku is string => typeof sku === 'string');
        } catch {
          // A contagem ainda permite informar o aviso quando a lista não estiver disponível.
        }
        const restantes = Math.max(0, quantidadeSemFotos - skusSemFotos.length);
        const detalhe = skusSemFotos.length
          ? `: ${skusSemFotos.join(', ')}${restantes ? ` (e mais ${restantes})` : ''}`
          : '';
        setAviso(`${quantidadeSemFotos} ${quantidadeSemFotos === 1 ? 'SKU sem foto de alta' : 'SKUs sem fotos de alta'}${detalhe}. ${quantidadeSemFotos === itens.length ? 'O ZIP não contém imagens.' : 'Esses produtos não foram incluídos no ZIP.'}`);
      }
      saveAs(data, 'imagens-amazon-vendor.zip');
      setMensagem(quantidadeSemFotos === itens.length
        ? 'ZIP gerado, mas nenhum produto tinha fotos de alta disponíveis.'
        : 'ZIP preparado com as fotos disponíveis em JPG e nomes ordenados para a Amazon Vendor.');
    } catch (error) {
      if (controller.signal.aborted) return;
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        navigate('/login?logout=true');
      } else if (axios.isAxiosError(error) && error.response?.status === 422 && error.response.data instanceof Blob) {
        try {
          const resposta = JSON.parse(await error.response.data.text());
          setErro(`SKUs sem fotos de alta: ${(resposta.skus || []).join(', ')}. Corrija a planilha ou as imagens e tente novamente.`);
        } catch {
          setErro('Alguns SKUs estão sem fotos de alta. Confira a planilha e tente novamente.');
        }
      } else {
        setErro('Não foi possível preparar o ZIP. Confira a planilha e tente novamente.');
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setProcessando(false);
        setProgresso('');
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, md: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ color: '#1a3661', fontWeight: 700, mb: 1 }}>Imagens Amazon Vendor</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Importe uma planilha com SKU e ASIN para baixar as fotos originais de alta em um único ZIP.</Typography>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography fontWeight={600}>1. Prepare a planilha</Typography>
            <Typography variant="body2" color="text.secondary">Use as colunas SKU e ASIN na primeira linha. Aceitamos Excel (.xlsx, .xls) ou CSV, com até {MAX_ITENS_AMAZON} produtos.</Typography>
          </Box>
          <Button variant="outlined" onClick={baixarModelo} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>Baixar modelo CSV</Button>
          <Box>
            <Typography fontWeight={600} sx={{ mb: 1 }}>2. Importe o arquivo</Typography>
            <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={processando} sx={{ textTransform: 'none', bgcolor: '#1a3661' }}>
              Selecionar Excel ou CSV
              <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={importar} />
            </Button>
          </Box>
          {nomeArquivo && <Typography variant="body2">{nomeArquivo} · {itens.length} {itens.length === 1 ? 'produto' : 'produtos'}</Typography>}
          {erro && <Alert severity="error" onClose={() => setErro('')}>{erro}</Alert>}
          {aviso && <Alert severity="warning">{aviso}</Alert>}
          {mensagem && <Alert severity="success" onClose={() => setMensagem('')}>{mensagem}</Alert>}
          {processando && <Box role="status"><Typography variant="body2" sx={{ mb: 1 }}>{progresso}</Typography><LinearProgress /></Box>}
          {itens.length > 0 && <>
            <Box>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Prévia dos primeiros produtos</Typography>
              <Table size="small" aria-label="Prévia dos SKUs e ASINs"><TableHead><TableRow><TableCell>SKU</TableCell><TableCell>ASIN</TableCell><TableCell>Arquivos gerados</TableCell></TableRow></TableHead>
                <TableBody>{itens.slice(0, 10).map(item => <TableRow key={item.sku}><TableCell>{item.sku}</TableCell><TableCell>{item.asin}</TableCell><TableCell>{item.asin}.MAIN.jpg, {item.asin}.PT01.jpg…</TableCell></TableRow>)}</TableBody>
              </Table>
            </Box>
            <Typography variant="body2" color="text.secondary">A foto _A de alta vira MAIN quando disponível; caso contrário, usamos a primeira foto. As demais seguem a ordem do nome como PT01, PT02… Se algum SKU estiver sem fotos de alta, você verá a lista antes de baixar.</Typography>
            <Stack direction="row" gap={1}>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={prepararZip} disabled={processando} sx={{ textTransform: 'none', bgcolor: '#1a3661' }}>Gerar e baixar ZIP</Button>
              {processando && <Button onClick={() => { controllerRef.current?.abort(); setProcessando(false); setProgresso(''); }}>Cancelar</Button>}
            </Stack>
          </>}
        </Stack>
      </Paper>
    </Container>
  );
}
